import { Link, Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Clapperboard,
  ImageIcon,
  LogOut,
  MessageCircle,
  ShieldAlert,
  ShieldCheck,
  Tag,
  UserCog,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { Button, LoadingState, PixlideLogo } from '@pixlide/ui';
import { useCurrentUser, useLogout } from '@/api/queries/auth';
import { useAuthStore } from '@/stores/auth-store';

interface NavEntry {
  to: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * « Utilisateurs » (comptes des clients) et « Équipe » (comptes back-office Pixlide) sont deux
 * entrées distinctes parce qu'elles ne gèrent pas les mêmes comptes ni les mêmes actions. La
 * liste des comptes clients eux-mêmes n'a pas d'entrée : on y accède depuis l'écran
 * « Utilisateurs », le back-office travaillant sur des personnes bien plus souvent que sur des
 * sociétés.
 */
const nav: NavEntry[] = [
  { to: '/utilisateurs', label: 'Utilisateurs', icon: <Users className="size-4.5" /> },
  { to: '/messages', label: 'Messages', icon: <MessageCircle className="size-4.5" /> },
  { to: '/plans', label: 'Offres', icon: <Tag className="size-4.5" /> },
  { to: '/templates', label: 'Templates vidéo', icon: <Clapperboard className="size-4.5" /> },
  { to: '/templates-habillage', label: "Templates d'habillage", icon: <ImageIcon className="size-4.5" /> },
  { to: '/equipe', label: 'Équipe', icon: <UserCog className="size-4.5" /> },
  { to: '/securite', label: 'Sécurité', icon: <ShieldCheck className="size-4.5" /> },
];

/**
 * Coquille de l'espace admin.
 *
 * Le marqueur « Back-office Pixlide » est délibérément appuyé (bandeau de couleur, icône
 * dédiée) : cette app tourne sur un port distinct de `frontend/` mais partage EXACTEMENT le
 * même design system (`@pixlide/ui`) — sans ce repère visuel constant, une personne qui a
 * les deux onglets ouverts pourrait confondre l'espace client et l'espace d'administration
 * de la plateforme, avec des conséquences bien plus lourdes qu'une simple erreur de
 * navigation (suspendre le mauvais tenant, par exemple).
 */
export function AdminShell() {
  const isBootstrapped = useAuthStore((state) => state.isBootstrapped);
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const logout = useLogout();
  const routerState = useRouterState({ select: (state) => state.location.pathname });
  const href = useRouterState({ select: (state) => state.location.href });

  // `bootstrapSession` (dans `main.tsx`) ne restaure que le jeton, jamais le profil : sans
  // cet appel, monté une fois pour toute la coquille admin, `user` resterait `null` en
  // permanence et la vérification de la permission `platform:admin` ci-dessous ne pourrait
  // jamais aboutir.
  const currentUser = useCurrentUser();

  const hasCheckedPermission = !isBootstrapped ? false : accessToken === null ? true : currentUser.isFetched;
  const isForbidden = hasCheckedPermission && user !== null && !user.permissions.includes('platform:admin');

  useEffect(() => {
    if (isForbidden) {
      void navigate({ to: '/interdit' });
    }
  }, [isForbidden, navigate]);

  if (!isBootstrapped || !hasCheckedPermission) {
    return <LoadingState label="Vérification des droits d'accès…" />;
  }

  // Garde RÉACTIVE, complément indispensable de `requirePlatformAdmin` : le `beforeLoad` du
  // routeur ne s'exécute qu'à la navigation. Il laisse donc passer le premier chargement
  // (lancé avant la fin de la restauration de session), et ne voit rien d'une session
  // perdue en cours de route — déconnexion, refresh refusé, 401 définitif, autre onglet.
  if (accessToken === null) {
    return <Navigate to="/connexion" search={{ redirect: href }} replace />;
  }

  return (
    <div className="flex min-h-dvh">
      <aside className="hidden w-64 shrink-0 flex-col gap-6 border-r border-ink-100/70 bg-white/70 px-4 py-6 backdrop-blur-sm lg:flex">
        <div className="flex flex-col gap-1 px-3">
          <PixlideLogo />
          <span className="inline-flex w-fit items-center gap-1.5 rounded-pill bg-ink-900 px-2.5 py-1 text-[10px] font-semibold tracking-wide text-white uppercase">
            <ShieldAlert aria-hidden className="size-3" />
            Back-office
          </span>
        </div>

        <nav aria-label="Navigation du back-office" className="flex flex-1 flex-col gap-0.5">
          <ul className="flex flex-col gap-0.5">
            {nav.map((entry) => (
              <li key={entry.to}>
                <Link
                  to={entry.to}
                  className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-ink-500 transition hover:bg-ink-50 hover:text-ink-900"
                  activeProps={{ className: 'bg-ink-50 font-medium text-ink-900' }}
                >
                  <span aria-hidden className="text-ink-300 group-hover:text-ink-700">
                    {entry.icon}
                  </span>
                  {entry.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {user ? (
          <div className="flex items-center gap-2 border-t border-ink-100/70 pt-4">
            <span
              aria-hidden
              className="grid size-9 shrink-0 place-items-center rounded-pill bg-brand-100 text-xs font-semibold text-brand-700"
            >
              {user.initials}
            </span>
            <span className="min-w-0 flex-1 truncate text-xs text-ink-500">
              {user.firstName} {user.lastName}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => logout.mutate()}
              isLoading={logout.isPending}
              aria-label="Déconnexion"
            >
              <LogOut aria-hidden className="size-4" />
            </Button>
          </div>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {user && !user.twoFactorEnabled && routerState !== '/securite' ? (
          <div className="flex items-center justify-between gap-4 bg-danger-500 px-6 py-2.5 text-sm text-white">
            <span>
              La double authentification est obligatoire pour ce compte : aucune action
              back-office n'est possible tant qu'elle n'est pas activée.
            </span>
            <Link
              to="/securite"
              className="shrink-0 rounded-pill bg-white/15 px-3 py-1 text-xs font-semibold whitespace-nowrap hover:bg-white/25"
            >
              Activer maintenant
            </Link>
          </div>
        ) : null}

        <main key={routerState} className="flex-1 px-6 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
